import * as resolvers from 'app/infrastructure/ui/renderer/resolvers';
import { columnName, tableName } from 'app/area/case/case-data-model';
import { getAllEntitiesAtPath, getChildEntities } from 'app/infrastructure/store';
import { canAddEntry, findOpenEntry, readStoreField } from 'app/area/case/features/entry-log/helpers';
import { reportTypes } from 'app/area/favourite/constants';

export const involvedPersonPath = (caseState, uiEngineProps) => {
  if (uiEngineProps?.hostProps?.()?.selectedReport !== reportTypes.involvedPersonReport) {
    return { involvedPersonPath: undefined };
  }

  const persons = getAllEntitiesAtPath(
    resolvers.getCaseData(caseState, uiEngineProps),
    [tableName.trans, tableName.involvedPerson]
  );

  return { involvedPersonPath: persons.length === 1 ? persons[0].id : undefined };
};

export const entryLogValues = (caseState, uiEngineProps) => ({
  entryLogValues: {
    entryTimeRecordedBy: resolvers.getComponentValues(
      caseState, uiEngineProps, columnName[tableName.involvedPersonInside].entryTimeRecordedBy
    ).valueItem,
    egressTime: resolvers.getComponentValues(
      caseState, uiEngineProps, columnName[tableName.involvedPersonInside].egressTime
    ).value,
    egressTimeRecordedBy: resolvers.getComponentValues(
      caseState, uiEngineProps, columnName[tableName.involvedPersonInside].egressTimeRecordedBy
    ).valueItem
  }
});

const findEntryLogDescriptor = (uiEngineProps) =>
  uiEngineProps?.detailsViewDescriptor?.children?.find(
    row =>
      JSON.stringify(row.entityTypePath) ===
      JSON.stringify(['TblTrans', tableName.involvedPerson, tableName.involvedPersonInConfinedSpace
      ])
  );

const isInvolvedPersonPath = (rowPath) =>
  !!rowPath?.length && !!rowPath[rowPath.length - 1]?.[tableName.involvedPerson];

const entryLogReadOnlyChecks = (caseState, uiEngineProps, context) => {
  const descriptor = findEntryLogDescriptor(uiEngineProps);
  const isReadOnlyAt = (path) =>
    !!resolvers.getComponentMetadata(caseState, {
      componentDescriptor: descriptor,
      renderMode: caseState.renderMode,
      hostProps: uiEngineProps.hostProps,
      resolveContext: { ...context, path }
    }).readOnly;

  return {
    descriptor,
    isTileReadOnly: () => !!descriptor?.properties?.readOnly || isReadOnlyAt(context.path),
    isEntryReadOnly: (entry) => !!entry && isReadOnlyAt(entry.id)
  };
};

export const canAddNewEntry = (caseState, uiEngineProps) => {
  const context = resolvers.getEntityContext(caseState, uiEngineProps);

  if (!isInvolvedPersonPath(context?.path)) {
    return {};
  }

  const readOnly = entryLogReadOnlyChecks(caseState, uiEngineProps, context);
  const entries = getChildEntities(
    resolvers.getCaseData(caseState, uiEngineProps),
    context.path,
    tableName.involvedPersonInConfinedSpace
  );
  const openEntry = findOpenEntry(entries, readStoreField);
  const tileReadOnly = readOnly.isTileReadOnly();

  return {
    canAddNewEntry: !!readOnly.descriptor && !tileReadOnly && canAddEntry(entries, readStoreField),
    openEntryPath: tileReadOnly || readOnly.isEntryReadOnly(openEntry) ? undefined : openEntry?.id
  };
};

/**
 * Provides all entry log rows (siblings of the current row) under the same parent.
 */
export const entryLogValuesAllRows = (caseState, uiEngineProps) => {
  const caseData = resolvers.getCaseData(caseState, uiEngineProps);
  const entityContext = resolvers.getEntityContext(caseState, uiEngineProps);
  const rowPath = entityContext.path;
  const parentPath = rowPath.slice(0, rowPath.length - 1);
  // The entity type is taken from the row's own store key, so siblings are found regardless of its exact name.
  const rowEntityType = Object.keys(rowPath[rowPath.length - 1] || {})[0];

  return {
    entryLogAllRows: rowEntityType ? getChildEntities(caseData, parentPath, rowEntityType) : []
  };
};
